package meineglasfaser.frontend.admin.pages.external

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import meineglasfaser.api.SurveyEntry
import meineglasfaser.api.SurveyInstallationTypeWithOptions
import meineglasfaser.api.YaTableOptions
import meineglasfaser.api.routes.ExternalRoutes
import meineglasfaser.api.routes.ShopRoutes
import meineglasfaser.frontend.admin.components.widgets.YaTable
import meineglasfaser.frontend.admin.components.widgets.named
import meineglasfaser.frontend.admin.components.widgets.sortedDesc
import meineglasfaser.frontend.common.BackendApi
import meineglasfaser.frontend.common.components.widgets.*
import meineglasfaser.frontend.common.components.yaform.*
import meineglasfaser.frontend.common.setJsonBody
import meineglasfaser.frontend.common.withAuth
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.*

@Page
@Composable
fun ExternalLahoPage(ctx: PageContext) {
    var showCreateModal by remember { mutableStateOf(false) }
    var showUpdateModal by remember { mutableStateOf<SurveyEntry?>(null) }
    val typesWithOptions = rememberQuery<List<SurveyInstallationTypeWithOptions>>(emptyList()) {
        BackendApi.get(ShopRoutes.SurveyTypesWithOptions())
    }

    YaTable(
        header = {
            H1 { Text("Lassnitzhöhe Bestellungen") }
        },
        idProperty = SurveyEntry::id,
        columns = listOfNotNull(
            SurveyEntry::name named "Name",
            SurveyEntry::installationAddress named "Installationsadresse",
            SurveyEntry::email named "E-Mail",
            SurveyEntry::phone named "Telefon",
            SurveyEntry::installationType named "Anschlussart",
            SurveyEntry::installationOption named "Anschlussoption",
            SurveyEntry::installationCount named "Anzahl Anschlüsse",
            SurveyEntry::createdAt named "Eingetragen am",
        ),
        initialOptions = YaTableOptions.DEFAULT.copy(
            orderBy = listOf(SurveyEntry::createdAt.sortedDesc())
        ),
        fetch = { options ->
            BackendApi.post(ExternalRoutes.Laho()) {
                withAuth()
                setJsonBody(options)
            }
        },
        onNewEntry = {
            showCreateModal = true
        },
        onViewEntry = { entry -> showUpdateModal = entry },
    )

    if (showCreateModal) {
        SurveyEntryCreateModal(typesWithOptions) { showCreateModal = false }
    }
}

@Composable
private fun SurveyEntryCreateModal(
    typesWithOptions: List<SurveyInstallationTypeWithOptions>,
    onHide: () -> Unit,
) {
    Modal(onHide) {
        YaForm(
            onSubmitScope = { data ->
                val body = ShopRoutes.SurveyEntry.RequestBody(
                    name = data.string(ShopRoutes.SurveyEntry.RequestBody::name),
                    email = data.string(ShopRoutes.SurveyEntry.RequestBody::email),
                    phone = data.string(ShopRoutes.SurveyEntry.RequestBody::phone),
                    installationTypeId = data.stringOrNull(ShopRoutes.SurveyEntry.RequestBody::installationTypeId),
                    installationOptionId = data.stringOrNull(ShopRoutes.SurveyEntry.RequestBody::installationOptionId),
                    installationCount = data.intOrNull(ShopRoutes.SurveyEntry.RequestBody::installationCount) ?: 1,
                    installationAddressStreetName = data.string(ShopRoutes.SurveyEntry.RequestBody::installationAddressStreetName),
                    installationAddressPostalCode = data.string(ShopRoutes.SurveyEntry.RequestBody::installationAddressPostalCode),
                    installationAddressCityName = data.string(ShopRoutes.SurveyEntry.RequestBody::installationAddressCityName),
                    installationAddressCountryCode = data.string(ShopRoutes.SurveyEntry.RequestBody::installationAddressCountryCode),
                    datenverarbeitungConfirmation = data.boolean(ShopRoutes.SurveyEntry.RequestBody::datenverarbeitungConfirmation),
                    termsConfirmation = data.boolean(ShopRoutes.SurveyEntry.RequestBody::termsConfirmation),
                    privacyConfirmation = data.boolean(ShopRoutes.SurveyEntry.RequestBody::privacyConfirmation),
                    grabungsarbeitenConfirmation = data.boolean(ShopRoutes.SurveyEntry.RequestBody::grabungsarbeitenConfirmation),
                    notes = data.stringOrNull(ShopRoutes.SurveyEntry.RequestBody::notes),
                )
                BackendApi.post(ExternalRoutes.Laho.New()) {
                    setJsonBody(body)
                    withAuth()
                }.fold(
                    onSuccess = {
                        Toasts.display(
                            Toast.success(
                                title = "Bestellung erfolgreich eingetragen",
                            )
                        )
                        onHide()
                    },
                    onFailure = {
                        Toasts.display(
                            Toast.error(
                                title = "Fehler beim Eintragen der Bestellung",
                            )
                        )
                    }
                )
            }
        ) {
            val selectedType = state.data[ShopRoutes.SurveyEntry.RequestBody::installationTypeId.name]
            LaunchedEffect(selectedType) {
                if (selectedType != null) {
                    val typeWithOptions = typesWithOptions.find { it.type.id == selectedType }
                    if (typeWithOptions != null) {
                        if (typeWithOptions.options.size == 1) {
                            // If there's only one option for the selected type, select it automatically
                            state.data[ShopRoutes.SurveyEntry.RequestBody::installationOptionId.name] = typeWithOptions.options[0].id
                        } else {
                            // Clear the option selection if the type changes
                            state.data.remove(ShopRoutes.SurveyEntry.RequestBody::installationOptionId.name)
                        }
                    }
                } else {
                    // Clear the option selection if no type is selected
                    state.data.remove(ShopRoutes.SurveyEntry.RequestBody::installationOptionId.name)
                }
            }
            Header {
                ModalCloseBtn(onHide)
                H3 { Text("Lassnitzhöhe Bestellung") }
            }
            Article {
                Header { Text("1. Anschlussart") }
                YaFormDropdown(
                    prop = ShopRoutes.SurveyEntry.RequestBody::installationTypeId,
                    label = { Text("Anschlussart") },
                    options = typesWithOptions.map { it.type.id to it.type.title },
                    required = false,
                )
                selectedType?.let { typeId ->
                    val typeWithOptions = typesWithOptions.find { it.type.id == typeId }
                    if (typeWithOptions != null) {
                        YaFormDropdown(
                            prop = ShopRoutes.SurveyEntry.RequestBody::installationOptionId,
                            label = { Text("Anschlussoption") },
                            options = typeWithOptions.options.map { option ->
                                option.id to buildString {
                                    append(option.title)
                                    if (option.priceText.isNotBlank()) {
                                        append(" (${option.priceText})")
                                    }
                                }
                            },
                            required = true,
                        )
                        if (typeWithOptions.type.footnote.isNotBlank()) {
                            Small { Text(typeWithOptions.type.footnote) }
                        }
                        if (typeWithOptions.type.requiresCountInput) {
                            YaFormNumberInput(
                                prop = ShopRoutes.SurveyEntry.RequestBody::installationCount,
                                label = { Text("Anzahl der Anschlüsse") },
                                min = 1,
                                required = true,
                            )
                        }
                    }
                }
            }
            Article {
                Header { Text("2. Adresse des Anschlusses") }
                UBLPostalAddressInputs(
                    streetName = ShopRoutes.SurveyEntry.RequestBody::installationAddressStreetName,
                    postalZone = ShopRoutes.SurveyEntry.RequestBody::installationAddressPostalCode,
                    cityName = ShopRoutes.SurveyEntry.RequestBody::installationAddressCityName,
                    countryCode = ShopRoutes.SurveyEntry.RequestBody::installationAddressCountryCode,
                )
            }
            Article {
                Header { Text("3. Kontakt") }
                YaFormTextInput(
                    prop = ShopRoutes.SurveyEntry.RequestBody::name,
                    label = { Text("Name / Firma") },
                    type = InputType.Text,
                    required = true,
                )
                YaFormTextInput(
                    prop = ShopRoutes.SurveyEntry.RequestBody::email,
                    label = { Text("E-Mail") },
                    type = InputType.Email,
                    required = true,
                )
                YaFormTextInput(
                    prop = ShopRoutes.SurveyEntry.RequestBody::phone,
                    label = { Text("Telefonnummer") },
                    type = InputType.Tel,
                    required = true,
                )
            }
            Article {
                Header { Text("4. Sonstiges") }
                YaFormTextArea(
                    prop = ShopRoutes.SurveyEntry.RequestBody::notes,
                    label = { Text("Anmerkungen") },
                    required = false,
                )
            }
            ModalFooterButtons(onHide, submitLabel = "Bestellung eintragen", resetLabel = "Zurücksetzen")
        }
    }
}
